/**
 * PULSANTE "SALVA TAGLI" (6 ott 2026, richiesta di Davide): sotto il riquadro prezzo / "Aggiungi al carrello".
 * Premendolo:
 *  1) chiede a ShapeDiver le esportazioni DXF della configurazione corrente (taglio laser e, se c'e' una dedica,
 *     la dedica): ogni esportazione costa un calcolo di crediti;
 *  2) manda gli indirizzi dei file e il riassunto delle scelte al servizio Eidos (worker, /salva-tagli), che
 *     divide il taglio in "taglio_esterno.dxf" (profilo esterno di ogni pezzo) e "tagli_interni.dxf" (tutti gli
 *     altri tagli), aggiunge la SCHEDA DI PRODUZIONE in PDF (7 ott: misure, superficie in dm2 per la galvanica,
 *     peso, disegno del taglio; i dati vengono dall'uscita "Scheda" del modello), fa uno zip e lo salva su Cloudflare;
 *  3) mostra sotto il pulsante il link "Scarica lo zip" (niente email: il browser non puo' spedire posta e
 *     Davide, 6 ott, ha preferito il solo link).
 * Il riquadro prezzo e' un "anchor" disegnato dall'App Builder: il pulsante ci viene aggiunto con un portale React.
 */
// ⚠️ niente componenti Mantine qui: questo componente sta FUORI dal MantineProvider (come EidosTotem) e con
// <Button> di Mantine l'intera pagina andava in errore ("MantineProvider was not found"). Solo HTML + CSS (totem.css).
import {useShapeDiverStoreParameters} from "@AppBuilderLib/entities/parameter/model/useShapeDiverStoreParameters";
import {useShapeDiverStoreSession} from "@AppBuilderLib/entities/session/model/useShapeDiverStoreSession";
import {IShapeDiverParameter} from "@AppBuilderLib/entities/parameter/config/parameter";
import {useEffect, useState} from "react";
import {createPortal} from "react-dom";
import {fotoCliente, inBase64} from "./fotoCliente";

const URL_SERVIZIO = "https://eidos-strade.eidos-direzionale.workers.dev/salva-tagli";
const EXPORT_TAGLIO = "EXP_TaglioLaser_DXF";
const EXPORT_DEDICA = "EXP_Dedica_DXF";
/** (12 ott, Gioiello Sagoma) contorni dell'incisione della foto: nello zip diventano "tagli_hatch.dxf" */
const EXPORT_HATCH = "EXP_Sagoma_DXF";
/** le scelte che finiscono nel riassunto (nome mostrato al cliente -> etichetta nel file) */
const SCELTE = ["Modello", "Forma", "Forma anello", "Misura", "Taglia anello", "Taglia bracciale", "Lunghezza collana", "Quantita orecchini", "Finitura", "Smalto", "Segnaposto", "Zoom mappa", "Posizione del punto", "Indirizzo", "Dedica", "Incisione", "Nome", "Carattere", "Allineamento", "Cornice", "Soglia", "Zoom sagoma", "Sposta X", "Sposta Y"];
/** (7 ott 2026) uscita dati "Scheda" del modello (dalla v35): misure, superficie in dm2, volume, peso -> PDF della scheda di produzione */
const USCITA_SCHEDA = "Scheda";

/** il testo JSON dell'uscita dati "Scheda" della sessione (null se il modello non ce l'ha) */
function leggiScheda(): string | null {
	try {
		const {sessions} = useShapeDiverStoreSession.getState();
		for (const s of Object.values(sessions)) {
			const outputs = (s as unknown as {outputs?: Record<string, {name?: string; content?: {data?: unknown}[]}>}).outputs ?? {};
			for (const o of Object.values(outputs)) {
				if (o.name !== USCITA_SCHEDA) continue;
				const d = o.content?.[0]?.data;
				if (d === undefined || d === null) return null;
				return typeof d === "string" ? d : JSON.stringify(d);
			}
		}
	} catch {
		/* ignora */
	}
	return null;
}

type Stato = {fase: "pronto" | "in corso" | "fatto" | "errore"; testo?: string; link?: string | null};

function leggiScelta(nome: string): string | null {
	const {parameterStores, getParameter} = useShapeDiverStoreParameters.getState();
	for (const ns of Object.keys(parameterStores)) {
		const st = getParameter(ns, nome);
		if (!st) continue;
		const p = st.getState() as IShapeDiverParameter<string>;
		const v = String(p.state.execValue ?? p.state.uiValue ?? "");
		const scelte = (p.definition as {choices?: string[]}).choices;
		if (scelte && scelte.length) {
			const i = parseInt(v, 10);
			return isNaN(i) ? v : (scelte[i] ?? v);
		}
		return v;
	}
	return null;
}

async function esporta(nome: string): Promise<string | null> {
	const {exportStores} = useShapeDiverStoreParameters.getState();
	for (const ns of Object.keys(exportStores)) {
		for (const st of Object.values(exportStores[ns])) {
			const e = st.getState() as {
				definition: {name?: string; displayname?: string};
				actions: {request: () => Promise<{content?: {href: string}[]}>};
			};
			if (e.definition.name === nome || e.definition.displayname === nome) {
				const r = await e.actions.request();
				return r.content?.[0]?.href ?? null;
			}
		}
	}
	return null;
}

/** trova (quando c'e') il riquadro prezzo disegnato dall'App Builder e vi aggiunge un contenitore per il pulsante */
function usaContenitore(): HTMLElement | null {
	const [el, setEl] = useState<HTMLElement | null>(null);
	useEffect(() => {
		let vivo = true;
		const cerca = () => {
			const paper = document.querySelector<HTMLElement>('.mantine-Group-root:has(> .mantine-Paper-root [class*="markdownNormalize"]) > .mantine-Paper-root');
			if (!paper) return false;
			// il pulsante va nella colonna INTERNA dove stanno i testi e "Aggiungi al carrello" (Stack > Paper > testo),
			// cosi' finisce sotto il carrello e segue la stessa griglia (totem.css)
			const testo = paper.querySelector<HTMLElement>('[class*="markdownNormalize"]');
			const colonna = testo?.closest<HTMLElement>(".mantine-Paper-root")?.parentElement ?? paper.querySelector<HTMLElement>(".mantine-Stack-root") ?? paper;
			let box = colonna.querySelector<HTMLElement>(".eidos-salva-tagli");
			if (!box) {
				box = document.createElement("div");
				box.className = "eidos-salva-tagli";
				colonna.appendChild(box);
			}
			// deve restare l'ULTIMO della colonna: sotto "Aggiungi al carrello", che React puo' aggiungere dopo di noi
			if (colonna.lastElementChild !== box) colonna.appendChild(box);
			if (vivo) setEl(box);
			return true;
		};
		if (!cerca()) {
			const t = window.setInterval(() => {
				if (cerca()) window.clearInterval(t);
			}, 500);
			window.setTimeout(() => window.clearInterval(t), 60000);
		}
		// il riquadro prezzo viene ridisegnato a ogni calcolo: si ricontrolla posizione e presenza del contenitore.
		// (6 ott pomeriggio, revisione) raggruppato: una ricerca ogni 150 ms al massimo, non una per ogni mutazione
		// del documento (la ricerca usa un selettore :has() su tutta la pagina)
		let attesa: number | undefined;
		const oss = new MutationObserver(() => {
			if (attesa !== undefined) return;
			attesa = window.setTimeout(() => {
				attesa = undefined;
				if (vivo) cerca();
			}, 150);
		});
		oss.observe(document.body, {childList: true, subtree: true});
		return () => {
			vivo = false;
			oss.disconnect();
			if (attesa !== undefined) window.clearTimeout(attesa);
		};
	}, []);
	return el;
}

/** fa partire il download dello zip (il servizio lo manda come allegato: il browser lo scarica senza cambiare pagina) */
function scarica(link: string, nome: string) {
	try {
		const a = document.createElement("a");
		a.href = link;
		a.download = nome;
		a.rel = "noopener";
		document.body.appendChild(a);
		a.click();
		window.setTimeout(() => a.remove(), 2000);
	} catch {
		window.open(link, "_blank");
	}
}

/**
 * (7 ott 2026, Davide: "visibile al pubblico deve restare solo Aggiungi al carrello: Salva tagli va nascosto, sono
 * informazioni nostre") Il pulsante compare SOLO sui browser di Eidos: si accende aprendo il configuratore una volta
 * con `?tagli=1` nell'indirizzo (resta ricordato in quel browser), si spegne con `?tagli=0`. Mai sul totem.
 */
function salvaTagliAbilitato(): boolean {
	try {
		if (document.body.classList.contains("eidos-totem")) return false;
		const v = new URLSearchParams(window.location.search).get("tagli");
		if (v === "1") window.localStorage.setItem("eidosTagli", "1");
		else if (v === "0") window.localStorage.removeItem("eidosTagli");
		return window.localStorage.getItem("eidosTagli") === "1";
	} catch {
		return false;
	}
}

export default function EidosSalvaTagli() {
	const [abilitato] = useState<boolean>(() => salvaTagliAbilitato());
	if (!abilitato) return null;
	return <SalvaTagli />;
}

function SalvaTagli() {
	const box = usaContenitore();
	const [stato, setStato] = useState<Stato>({fase: "pronto"});

	const salva = async () => {
		if (stato.fase === "in corso") return;
		setStato({fase: "in corso"});
		try {
			const info: Record<string, string> = {};
			for (const n of SCELTE) {
				const v = leggiScelta(n);
				if (v !== null && v !== "") info[n] = v;
			}
			const dedicaAttiva = (info["Dedica"] ?? "").toLowerCase().startsWith("voglio") && (info["Incisione"] ?? "").trim() !== "";
			const taglio = await esporta(EXPORT_TAGLIO);
			if (!taglio) throw new Error("esportazione del taglio non disponibile");
			const dedica = dedicaAttiva ? await esporta(EXPORT_DEDICA) : null;
			// (12 ott) Gioiello Sagoma: i contorni dell'incisione (se il modello ha quell'esportazione; sul modello mappa non c'e')
			const hatch = await esporta(EXPORT_HATCH);
			// (7 ott) la scheda di produzione del modello (superficie dm2, peso...) viaggia con i file: il servizio fa il PDF
			const scheda = leggiScheda();
			// (14 ott, Týpos) la foto del cliente com'era e lo stencil preparato dal configuratore (vanno nello zip)
			const foto: Record<string, {nome: string; tipo: string; dati: string}> = {};
			if (fotoCliente.originale) foto.originale = {nome: fotoCliente.originale.name, tipo: fotoCliente.originale.type || "image/jpeg", dati: await inBase64(fotoCliente.originale)};
			if (fotoCliente.stencil) foto.stencil = {nome: fotoCliente.stencil.name, tipo: fotoCliente.stencil.type || "image/png", dati: await inBase64(fotoCliente.stencil)};
			const r = await fetch(URL_SERVIZIO, {
				method: "POST",
				headers: {"content-type": "application/json"},
				body: JSON.stringify({taglio, dedica, hatch, info, scheda, ...(Object.keys(foto).length ? {foto} : {})}),
				signal: AbortSignal.timeout(60000),
			});
			const j = (await r.json()) as {ok: boolean; errore?: string; link?: string | null; zip?: string};
			if (!r.ok || !j.ok) throw new Error(j.errore || "risposta " + r.status);
			// (6 ott, Davide) lo zip si scarica SUBITO; il link resta sotto per qualche secondo come riserva
			if (j.link) scarica(j.link, j.zip ?? "Eidos_tagli.zip");
			setStato({fase: "pronto"});
		} catch (e) {
			setStato({fase: "errore", testo: "Non riuscito: " + String((e as Error)?.message ?? e)});
			window.setTimeout(() => setStato((s) => (s.fase === "errore" ? {fase: "pronto"} : s)), 12000);
		}
	};

	if (!box) return null;
	// (6 ott, Davide: "deve salvare i tagli ma non deve far aprire altre cose") niente scritte ne' link: il pulsante
	// resta "Salva tagli", si spegne solo mentre lavora e lo zip parte da solo. Solo un errore viene mostrato.
	return createPortal(
		<div className="eidos-salva-tagli-blocco">
			<button type="button" className="eidos-salva-tagli-pulsante" onClick={salva} disabled={stato.fase === "in corso"}>
				Salva tagli
			</button>
			{stato.fase === "errore" && stato.testo && <div className="eidos-salva-tagli-esito errore">{stato.testo}</div>}
		</div>,
		box,
	);
}
